import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, FormControlLabel, Checkbox,
  Alert, TextField, InputAdornment, Collapse, Box, Divider, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const ACTIONS = {
  hide: {
    title: 'Hide demo data', button: 'Hide', color: 'warning', can: 'canHide',
    intro: 'Choose what to hide and for whom. Hidden accounts cannot sign in and their offerings, profiles and families are not shown; hidden offerings are simply switched off. Nothing is deleted.',
  },
  show: {
    title: 'Show demo data', button: 'Show', color: 'primary', can: 'canHide',
    intro: 'Choose the hidden demo data to switch back on.',
  },
  delete: {
    title: 'Delete demo data', button: 'Delete', color: 'error', can: 'canDelete',
    intro: 'Choose what to delete permanently and for whom. Deleting whole accounts removes everything they own; picking modules removes just that data and keeps the accounts.',
  },
  recreate: {
    title: 'Recreate demo data', button: 'Recreate', color: 'primary', can: 'canRecreate',
    intro: 'Choose what to bring back and for whom. Deleted records are restored, missing ones created and existing ones kept, so nothing is duplicated.',
  },
};

// Choose which demo data an action (hide, show, delete, recreate) applies to: what (whole
// accounts, or the data of particular modules/menus) and for whom (groups and, except when
// recreating, individual accounts).
export default function DemoDataScopeDialog({ action, status, busy, onClose, onConfirm }) {
  const cfg = ACTIONS[action] || ACTIONS.hide;
  const groups = status?.groups || [];
  const accounts = useMemo(() => (status?.accounts || []).filter(a => !a.kept), [status]);
  const available = useMemo(() => (status?.modules || []).filter(m => m[cfg.can]), [status, cfg]);

  const [modules, setModules] = useState(['accounts']);
  const [roles, setRoles] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [showAccounts, setShowAccounts] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const whole = modules.includes('accounts');
  // Which accounts the action can apply to
  const eligible = (a) => (!whole ? true : action === 'hide' ? a.isActive : action === 'show' ? !a.isActive : true);
  const byAccount = action !== 'recreate';

  // Start: whole accounts (plus lists & questions when recreating); for Show, offerings if only those are hidden
  useEffect(() => {
    if (!action) return;
    const onlyOfferingsHidden = action === 'show' && !accounts.some(a => !a.isActive) && status?.hiddenOfferings > 0;
    const startModules = onlyOfferingsHidden ? ['offerings'] : action === 'recreate' ? ['accounts', 'lists', 'questions'] : ['accounts'];
    setModules(startModules);
    const isWhole = startModules.includes('accounts');
    const elig = accounts.filter(a => (!isWhole ? (onlyOfferingsHidden ? a.role === 'vendor' : true)
      : action === 'hide' ? a.isActive : action === 'show' ? !a.isActive : true));
    setRoles(action === 'recreate' ? groups.map(g => g.role) : [...new Set(elig.map(a => a.role))]);
    setPicked(new Set(elig.map(a => a.id)));
    setShowAccounts(false); setSearch(''); setConfirmText('');
  }, [action]); // reset only when the dialog opens for an action

  if (!action) return null;
  const eligibleIn = (role) => accounts.filter(a => a.role === role && eligible(a));
  const toggleModule = (key, on) => setModules(ms => {
    let next = on ? [...new Set([...ms, key])] : ms.filter(k => k !== key);
    if (key === 'accounts' && on) next = next.filter(k => k === 'accounts' || available.find(m => m.key === k)?.config);
    return next;
  });
  const toggleRole = (role, on) => {
    setRoles(r => (on ? [...new Set([...r, role])] : r.filter(x => x !== role)));
    setPicked(p => {
      const next = new Set(p);
      eligibleIn(role).forEach(a => (on ? next.add(a.id) : next.delete(a.id)));
      return next;
    });
  };
  const toggleAccount = (a) => setPicked(p => {
    const next = new Set(p);
    if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
    return next;
  });

  const dataModules = available.filter(m => !m.config);
  const configModules = available.filter(m => m.config);
  const pickedData = modules.filter(k => dataModules.some(m => m.key === k));
  const inScope = accounts.filter(a => roles.includes(a.role) && eligible(a));
  const chosen = byAccount ? inScope.filter(a => picked.has(a.id)) : [];
  const q = search.trim().toLowerCase();
  const listed = inScope.filter(a => !q || [a.name, a.email].some(t => (t || '').toLowerCase().includes(q)));
  const needsAccounts = pickedData.length > 0;
  const nothing = !modules.length || (needsAccounts && (byAccount ? !chosen.length : !roles.length));
  const allGroups = roles.length === groups.length;
  const summary = whole ? 'whole accounts' : modules.map(k => available.find(m => m.key === k)?.label).filter(Boolean).join(', ');

  const confirm = () => onConfirm({
    modules,
    roles,
    // every account of the chosen groups = send just the groups; otherwise the accounts too
    ...(byAccount && chosen.length !== inScope.length && { userIds: chosen.map(a => a.id) }),
  });

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{cfg.title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>{cfg.intro}</Typography>
        {action === 'delete' && (
          <Alert severity="error" sx={{ mb: 2 }}>Deleting cannot be undone, except by "Recreate demo data".</Alert>
        )}

        {/* What */}
        <Typography variant="subtitle2" fontWeight={700}>What</Typography>
        {dataModules.map(m => {
          const covered = whole && m.key !== 'accounts';
          return (
            <FormControlLabel
              key={m.key} sx={{ display: 'flex', alignItems: 'flex-start', my: 0.25 }}
              control={<Checkbox size="small" sx={{ pt: 0.25 }} checked={modules.includes(m.key) || covered} disabled={covered}
                onChange={e => toggleModule(m.key, e.target.checked)} />}
              label={(
                <span>
                  <Typography component="span" variant="body2" fontWeight={m.key === 'accounts' ? 700 : 500}>{m.label}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{covered ? 'Included with the accounts' : m.menus}</Typography>
                </span>
              )}
            />
          );
        })}
        {action === 'hide' || action === 'show' ? (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Other data (guests, contracts, ceremonies…) has no on/off switch: to take it out of the application without losing it
            for good, hide the whole accounts; or delete it and recreate it later.
          </Typography>
        ) : null}
        {configModules.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={700} mt={1.5}>Application lists</Typography>
            {configModules.map(m => (
              <FormControlLabel
                key={m.key} sx={{ display: 'flex', alignItems: 'flex-start', my: 0.25 }}
                control={<Checkbox size="small" sx={{ pt: 0.25 }} checked={modules.includes(m.key)} onChange={e => toggleModule(m.key, e.target.checked)} />}
                label={<span><Typography component="span" variant="body2">{m.label}</Typography><Typography variant="caption" color="text.secondary" display="block">{m.menus}</Typography></span>}
              />
            ))}
          </>
        )}

        {/* For whom */}
        {needsAccounts && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>For whom</Typography>
              <Button size="small" onClick={() => groups.forEach(g => toggleRole(g.role, !allGroups))}>
                {allGroups ? 'Clear all' : 'Select all'}
              </Button>
            </Stack>
            {groups.map(g => {
              const inGroup = eligibleIn(g.role);
              const n = action === 'recreate' ? g.total : inGroup.length;
              const some = byAccount && inGroup.some(a => picked.has(a.id));
              const every = byAccount ? inGroup.length > 0 && inGroup.every(a => picked.has(a.id)) : roles.includes(g.role);
              return (
                <FormControlLabel
                  key={g.role} sx={{ display: 'flex' }}
                  control={(
                    <Checkbox
                      checked={roles.includes(g.role) && (every || !byAccount)}
                      indeterminate={byAccount && roles.includes(g.role) && some && !every}
                      disabled={byAccount && !n}
                      onChange={e => toggleRole(g.role, e.target.checked)}
                    />
                  )}
                  label={<span>{g.label} <Typography component="span" variant="caption" color="text.secondary">({n} {action === 'recreate' ? 'accounts now' : whole && action === 'hide' ? 'active' : whole && action === 'show' ? 'hidden' : 'accounts'})</Typography></span>}
                />
              );
            })}
            {action === 'recreate' && roles.some(r => ['bride_family', 'groom_family'].includes(r)) && (
              <Typography variant="caption" color="text.secondary" display="block">
                Families include the base sample wedding (Alice & Bob).
              </Typography>
            )}

            {byAccount && inScope.length > 0 && (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                  <Typography variant="body2"><strong>{chosen.length}</strong> of {inScope.length} account(s) selected</Typography>
                  <Button size="small" onClick={() => setShowAccounts(v => !v)}>{showAccounts ? 'Hide accounts' : 'Choose accounts'}</Button>
                </Stack>
                <Collapse in={showAccounts}>
                  <TextField size="small" fullWidth value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email" sx={{ my: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                  <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1 }}>
                    {listed.map(a => (
                      <FormControlLabel key={a.id} sx={{ display: 'flex', my: 0.25 }}
                        control={<Checkbox size="small" checked={picked.has(a.id)} onChange={() => toggleAccount(a)} />}
                        label={(
                          <span>
                            <Typography component="span" variant="body2" fontWeight={600}>{a.name}</Typography>{' '}
                            <Typography component="span" variant="caption" color="text.secondary">{a.email}</Typography>{' '}
                            <Chip size="small" label={a.roleLabel} sx={{ height: 18 }} />
                          </span>
                        )}
                      />
                    ))}
                    {!listed.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No accounts match.</Typography>}
                  </Box>
                </Collapse>
              </>
            )}
            {byAccount && !inScope.length && roles.length > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>There is nothing to {cfg.button.toLowerCase()} for the chosen groups.</Alert>
            )}
          </>
        )}

        {action === 'delete' && (
          <TextField fullWidth size="small" sx={{ mt: 2 }} label="Type DELETE to confirm" value={confirmText} onChange={e => setConfirmText(e.target.value)} />
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          {status?.keptAdmin} and your own account are never included.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>{summary}</Typography>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color={cfg.color} onClick={confirm}
          disabled={busy || nothing || (action === 'delete' && confirmText !== 'DELETE')}>
          {busy ? 'Working…' : byAccount && needsAccounts ? `${cfg.button} for ${chosen.length} account(s)` : cfg.button}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
